import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Settings,
  Workflow,
  Briefcase,
  Users,
  Contact,
  Phone,
  BarChart3,
  Lightbulb,
  Trophy,
  GitBranch,
  BookOpen,
  Library,
  UserCheck,
  Search,
  GraduationCap,
  MessageCircle,
  Calendar,
  Award,
  PlayCircle,
  ListPlus,
  History as HistoryIcon,
  BarChartHorizontal,
  Megaphone,
  BarChart,
  Image as ImageIcon,
  Plug,
  Star,
  Send,
  ListChecks,
  FileText,
  Palette,
  Cog,
  Target,
  Activity,
  Radar,
  Archive,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  getInitials,
  Tooltip,
} from "@/components/ui";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { WorkspaceBrand } from "@/components/layout/WorkspaceBrand";
import { SidebarItem } from "@/components/layout/SidebarItem";
import { LanguageToggle } from "@/components/layout/LanguageToggle";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/cn";

export type ModuleContext =
  | "sales"
  | "marketing"
  | "coach"
  | "settings";

interface NavItemDef {
  to: string;
  /** i18n key or literal (fallback) */
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
}

interface NavGroupDef {
  labelKey: string;
  items: NavItemDef[];
}

interface ModuleConfig {
  id: ModuleContext;
  labelKey: string;
  groups: NavGroupDef[];
  pinned?: NavItemDef[];
}

const SALES_GROUPS: NavGroupDef[] = [
  {
    labelKey: "nav.navigation",
    items: [
      { to: "/sales/dashboard", labelKey: "marketing.nav.dashboard", icon: BarChart3 },
      { to: "/sales/leads", labelKey: "chatbot.nav.leads", icon: Users },
      { to: "/sales/contacts", labelKey: "sales.contacts", icon: Contact },
      { to: "/sales/pipelines", labelKey: "sales.pipelines", icon: Workflow },
      { to: "/sales/deals", labelKey: "sales.deals", icon: Briefcase },
      { to: "/sales/calls", labelKey: "sales.calls", icon: Phone },
    ],
  },
  {
    labelKey: "nav.analysis",
    items: [
      { to: "/sales/custdev", labelKey: "sales.custdev", icon: Lightbulb },
      { to: "/sales/managers", labelKey: "sales.managers", icon: UserCheck },
      { to: "/sales/leaderboard", labelKey: "sales.leaderboard", icon: Trophy },
      { to: "/sales/plans", labelKey: "sales.plans", icon: Target },
      { to: "/sales/voronka-audit", labelKey: "sales.voronka", icon: GitBranch },
    ],
  },
  {
    labelKey: "nav.knowledge",
    items: [
      { to: "/sales/playbook", labelKey: "sales.playbook", icon: BookOpen },
      { to: "/sales/knowledge", labelKey: "chatbot.nav.knowledge", icon: Library },
      { to: "/sales/search", labelKey: "common.search", icon: Search },
    ],
  },
];

const MARKETING_GROUPS: NavGroupDef[] = [
  {
    labelKey: "nav.navigation",
    items: [
      { to: "/marketing", labelKey: "marketing.nav.dashboard", icon: BarChart, end: true },
      { to: "/marketing/campaigns", labelKey: "marketing.nav.campaigns", icon: Megaphone },
      { to: "/marketing/creatives", labelKey: "marketing.nav.creatives", icon: ImageIcon },
      { to: "/marketing/scenarios", labelKey: "marketing.nav.scenarios", icon: Sparkles },
      { to: "/marketing/competitors", labelKey: "marketing.nav.competitors", icon: Radar },
      { to: "/marketing/stories", labelKey: "marketing.nav.stories", icon: Archive },
    ],
  },
  {
    labelKey: "nav.salesData",
    items: [
      { to: "/sales-data/clients", labelKey: "marketing.nav.clients", icon: Users },
      { to: "/sales-data/summaries", labelKey: "marketing.nav.summaries", icon: FileText },
    ],
  },
];

// Chatbot moduli vaqtinchalik yashirilgan — keyingi sprint'da qaytariladi.
// CHATBOT_GROUPS, "chatbot" ModuleContext va detectModuleContext'dagi tegishli branch
// olib tashlangan. Backend /api/chatbot/* endpoint'lar saqlangan.

const COACH_GROUPS: NavGroupDef[] = [
  {
    labelKey: "nav.coach",
    items: [
      { to: "/sales/coach", labelKey: "coach.hub", icon: GraduationCap, end: true },
      { to: "/sales/coach/lessons", labelKey: "coach.lessons", icon: BookOpen },
      { to: "/sales/coach/chat", labelKey: "coach.chat", icon: MessageCircle },
      { to: "/sales/coach/attendance", labelKey: "coach.attendance", icon: Calendar },
      { to: "/sales/coach/certificates", labelKey: "coach.certificates", icon: Award },
    ],
  },
  {
    labelKey: "coach.exam",
    items: [
      { to: "/sales/coach/exam/start", labelKey: "coach.examStart", icon: PlayCircle },
      { to: "/sales/coach/exam/history", labelKey: "coach.history", icon: HistoryIcon },
      { to: "/sales/coach/scenarios", labelKey: "coach.scenarios", icon: ListPlus },
      { to: "/sales/coach/exam/stats", labelKey: "coach.stats", icon: BarChartHorizontal },
    ],
  },
];

// Interwork single-tenant — multi-tenant members/invitations olib tashlandi
const SETTINGS_GROUPS: NavGroupDef[] = [
  {
    labelKey: "nav.company",
    items: [
      { to: "/settings/general", labelKey: "settings.general", icon: Settings },
      { to: "/settings/branding", labelKey: "settings.branding", icon: Palette },
    ],
  },
  {
    labelKey: "nav.integration",
    items: [
      { to: "/settings/integrations", labelKey: "settings.integrations", icon: Plug },
      { to: "/settings/telegram", labelKey: "settings.telegram", icon: Send },
      {
        to: "/settings/sales/criteria",
        labelKey: "settings.salesCriteria",
        icon: ListChecks,
      },
      { to: "/settings/ai-costs", labelKey: "settings.aiCosts", icon: Activity },
    ],
  },
];

const SALES_PINNED: NavItemDef[] = [
  { to: "/sales/calls", labelKey: "sales.todayCalls", icon: Phone },
  { to: "/sales/deals", labelKey: "sales.activeDeals", icon: Briefcase },
];

const MODULE_CONFIGS: Record<ModuleContext, ModuleConfig> = {
  sales: {
    id: "sales",
    labelKey: "nav.sales",
    groups: SALES_GROUPS,
    pinned: SALES_PINNED,
  },
  marketing: {
    id: "marketing",
    labelKey: "nav.marketing",
    groups: MARKETING_GROUPS,
  },
  coach: { id: "coach", labelKey: "nav.coach", groups: COACH_GROUPS },
  settings: {
    id: "settings",
    labelKey: "nav.settings",
    groups: SETTINGS_GROUPS,
  },
};

/**
 * Pathname asosida joriy modul kontekstini aniqlaydi.
 * `/sales/coach/...` → "coach" (Sales emas, alohida kontekst)
 * Bosh sahifa yoki noma'lum yo'l → null (sidebar render qilinmaydi)
 */
export function detectModuleContext(pathname: string): ModuleContext | null {
  if (pathname.startsWith("/sales/coach")) return "coach";
  // /sales-data — marketing app ichidagi sotuv ma'lumotlari (sales emas)
  if (pathname.startsWith("/sales-data")) return "marketing";
  if (pathname.startsWith("/sales")) return "sales";
  if (pathname.startsWith("/marketing")) return "marketing";
  // /chatbot — moduli yashirilgan, sidebar render qilinmaydi (router redirect qiladi)
  if (pathname.startsWith("/chatbot")) return null;
  if (pathname.startsWith("/settings")) return "settings";
  return null;
}

interface SidebarProps {
  context: ModuleContext;
  collapsed?: boolean;
  /** Toggle handler — yig'ish/ochish (header tugmasi + Cmd/Ctrl+B). */
  onToggleCollapsed?: () => void;
}

/**
 * Sidebar — Anthropic redesign shell (Sprint R2, plan 6.1 va 9).
 *
 * Spec:
 *  - Width 272px (collapsed 64px icon-only), smooth width transition
 *  - Background --bg-surface, right border 1px --border-subtle
 *  - Top: brand logo (har doim ko'rinadi) + alohida collapse toggle
 *  - Group label uppercase 11px tracking 0.06em
 *  - Item 36px, active rail-left 2px coral (SidebarItem ichida)
 *  - "MASHHUR" pinned section (Sales modulida)
 *  - Sticky footer: user avatar + name + lang switcher + cog
 *
 * Sidebar ichidagi qidiruv (Cmd+K) va "Bosh sahifa" link olib tashlandi —
 * qidiruv TopBar + global ⌘K shortcut orqali CommandPalette'da saqlanadi.
 */
export function Sidebar({
  context,
  collapsed = false,
  onToggleCollapsed,
}: SidebarProps) {
  const { t } = useI18n();
  const config = useMemo(() => {
    return MODULE_CONFIGS[context];
  }, [context]);

  if (!config) return null;

  const moduleLabel = t(config.labelKey);
  const widthClass = collapsed ? "w-16" : "w-[276px]";

  return (
    <aside
      className={cn(
        "hidden md:flex shrink-0 flex-col border-r border-[var(--border-subtle)] bg-[var(--bg-surface)]",
        "sticky top-0 h-screen self-start",
        "transition-[width] duration-200 ease-out",
        widthClass,
      )}
      aria-label={moduleLabel}
    >
      <div
        className={cn(
          "shrink-0 border-b border-[var(--border-subtle)] pb-3 pt-5",
          collapsed
            ? "flex flex-col items-center gap-2 px-2"
            : "flex items-center justify-between gap-2 px-3",
        )}
      >
        <WorkspaceBrand collapsed={collapsed} />
        {onToggleCollapsed && (
          <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
        )}
      </div>

      <nav
        className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden",
          collapsed ? "px-2 py-3" : "px-3 py-3",
        )}
      >
        {config.groups.map((group) => (
          <div key={group.labelKey} className={cn("mb-4 last:mb-0")}>
            {!collapsed && (
              <div className="px-3 pb-2 pt-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                  {t(group.labelKey)}
                </span>
              </div>
            )}
            <ul className="space-y-1" role="list">
              {group.items.map((item) => (
                <li key={item.to}>
                  <SidebarItem
                    to={item.to}
                    label={t(item.labelKey)}
                    icon={item.icon}
                    end={item.end}
                    collapsed={collapsed}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}

        {config.pinned && config.pinned.length > 0 && (
          <div className="mb-4">
            {!collapsed ? (
              <div className="flex items-center gap-1.5 px-3 pb-2 pt-1">
                <Star
                  className="h-3.5 w-3.5 text-[var(--text-muted)]"
                  aria-hidden
                />
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("common.popular")}
                </span>
              </div>
            ) : (
              <div className="my-2 mx-auto h-px w-6 bg-[var(--border-subtle)]" />
            )}
            <ul className="space-y-1" role="list">
              {config.pinned.map((item) => (
                <li key={`pinned-${item.to}`}>
                  <SidebarItem
                    to={item.to}
                    label={t(item.labelKey)}
                    icon={item.icon}
                    collapsed={collapsed}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      <SidebarFooter collapsed={collapsed} />
    </aside>
  );
}

interface CollapseToggleProps {
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * CollapseToggle — sidebar yig'ish/ochish tugmasi (Cmd/Ctrl+B bilan bir).
 *
 * Brand logodan alohida — logo har doim ko'rinib turadi, bu tugma esa
 * mustaqil collapse holatini almashtiradi (collapsed → PanelLeftOpen
 * "Menyuni ochish", expanded → PanelLeftClose "Menyuni yopish").
 */
function CollapseToggle({ collapsed, onToggle }: CollapseToggleProps) {
  const { t } = useI18n();
  const label = collapsed ? t("common.openMenu") : t("common.closeMenu");
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <Tooltip content={label} side="right" shortcut="⌘B">
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-pressed={!collapsed}
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md outline-none transition-colors",
          // Collapsed: 40x40 tap target, nav ikonkalari bilan bir o'qda markazda
          collapsed ? "mx-auto h-10 w-10" : "h-8 w-8",
          "text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
          "focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60",
        )}
      >
        <ToggleIcon
          className={cn(collapsed ? "h-5 w-5" : "h-[18px] w-[18px]")}
          aria-hidden
        />
      </button>
    </Tooltip>
  );
}

interface SidebarFooterProps {
  collapsed: boolean;
}

function SidebarFooter({ collapsed }: SidebarFooterProps) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  if (!user) return null;

  return (
    <div
      className={cn(
        "shrink-0 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)]",
        collapsed ? "p-2" : "flex h-10 items-center gap-1 px-2",
      )}
    >
      {collapsed ? (
        <div className="flex flex-col items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={user.fullName}
                className={cn(
                  "mx-auto flex h-10 w-10 items-center justify-center rounded-md transition-colors",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60",
                )}
              >
                <Avatar size="xs">
                  {user.avatar ? (
                    <AvatarImage src={user.avatar} alt={user.fullName} />
                  ) : null}
                  <AvatarFallback>{getInitials(user.fullName)}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <UserMenuContent
              fullName={user.fullName}
              email={user.email}
              onLogout={() => logout()}
            />
          </DropdownMenu>
          <LanguageToggle />
          <Link
            to="/settings/general"
            aria-label={t("common.settings")}
            className={cn(
              "mx-auto flex h-10 w-10 items-center justify-center rounded-md text-[var(--text-muted)] transition-colors",
              "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60",
            )}
          >
            <Cog className="h-5 w-5" aria-hidden />
          </Link>
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={user.fullName}
                className={cn(
                  "group flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 transition-colors",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60",
                )}
              >
                <Avatar size="xs">
                  {user.avatar ? (
                    <AvatarImage src={user.avatar} alt={user.fullName} />
                  ) : null}
                  <AvatarFallback>{getInitials(user.fullName)}</AvatarFallback>
                </Avatar>
                <div className="flex min-w-0 flex-1 flex-col items-start">
                  <span className="truncate text-[12.5px] font-medium leading-tight text-[var(--text-primary)]">
                    {user.fullName}
                  </span>
                  <span className="truncate text-[10.5px] leading-tight text-[var(--text-muted)]">
                    {user.email}
                  </span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <UserMenuContent
              fullName={user.fullName}
              email={user.email}
              onLogout={() => logout()}
            />
          </DropdownMenu>

          <LanguageToggle />

          <Link
            to="/settings/general"
            aria-label={t("common.settings")}
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[var(--text-muted)] transition-colors",
              "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/60",
            )}
          >
            <Cog className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}

interface UserMenuContentProps {
  fullName: string;
  email: string;
  onLogout: () => void;
}

function UserMenuContent({ fullName, email, onLogout }: UserMenuContentProps) {
  const { t } = useI18n();
  return (
    <DropdownMenuContent align="start" sideOffset={8} className="min-w-[220px]">
      <DropdownMenuLabel>
        <div className="flex flex-col">
          <span className="text-sm font-medium normal-case tracking-normal text-[var(--text-primary)]">
            {fullName}
          </span>
          <span className="text-[11px] normal-case tracking-normal text-[var(--text-muted)]">
            {email}
          </span>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <Link to="/settings/general">
        <DropdownMenuItem>
          <Cog className="h-3.5 w-3.5" aria-hidden />
          {t("common.settings")}
        </DropdownMenuItem>
      </Link>
      <DropdownMenuSeparator />
      <DropdownMenuItem variant="danger" onSelect={onLogout}>
        {t("common.logout")}
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}
